/**
 * Shared dropdown paper/footer helpers for MemberAutocomplete
 * (matches Attorney / Admin-Learner selector footer UI).
 *
 * WHY THIS USES DOCUMENT/STYLE INJECTION INSTEAD OF makeStyles / sx
 * ----------------------------------------------------------------
 * MUI Autocomplete (lab v4) portals the list into `.MuiAutocomplete-paper`
 * outside the field's React tree (`disablePortal: false`). That creates three
 * constraints this module works around:
 *
 * 1. No first-class "footer" slot — Figma requires a sticky helper row
 *    ("Be specific…" / custom text + "Showing N/N Results") and a bottom
 *    gradient overlay inside the paper. Autocomplete only exposes listbox /
 *    option / input slots, so the footer must be appended to the paper node.
 *
 * 2. Portaled paper is hard to reach with scoped styles — parent `makeStyles`
 *    / `sx` do not style the portaled paper unless we use global selectors or
 *    a custom `PaperComponent`. Inline paper/listbox tweaks (radius, min-height
 *    by result count, full-bleed option padding) therefore run against the live
 *    DOM after open.
 *
 * 3. Footer copy must stay live while open — result count and left helper text
 *    change as search results update. Mutation against the open paper keeps
 *    parity with the existing Attorney / Admin-Learner modal behavior without
 *    rewriting those flows.
 *
 * Preferable long-term alternative: a React `PaperComponent` that owns the
 * footer + gradient as children, with paper/listbox/option styles in
 * `makeStyles` / `sx` (or theme overrides). Keep this util until that shared
 * Paper is adopted across MemberAutocomplete, AttorneyModal, and
 * AdminLearnerModal so all three stay visually consistent.
 */

export const MEMBER_AUTOCOMPLETE_FOOTER_STYLE_ID =
  'member-autocomplete-footer-styles';

/**
 * Global CSS for the autocomplete dropdown footer and scrollbars.
 * @returns {string}
 */
export const getMemberAutocompleteFooterStyles = () => `
  .MuiAutocomplete-paper .dropdown-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    display: flex;
    padding: 24px 20px 12px 20px;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    z-index: 2;
  }

  .MuiAutocomplete-paper .dropdown-footer .footer-left {
    color: var(--success-green-green-dark, #228053);
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0.24px;
    margin-right: auto;
  }

  .MuiAutocomplete-paper .dropdown-footer .footer-right {
    color: var(--primary-blue-primary-deep, #0F2C54);
    text-align: right;
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0.24px;
  }

  .MuiAutocomplete-noOptions {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 72px;
    padding: 16px 20px 56px;
    box-sizing: border-box;
    text-align: center;
  }

  .MuiAutocomplete-listbox {
    width: 100% !important;
    align-self: stretch !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  /*
   * Full-bleed option row: MUI wraps renderOption in <li.MuiAutocomplete-option>
   * with default padding. Zero that padding so hover/selected backgrounds cover
   * the entire row; content padding lives on the inner option content.
   */
  .MuiAutocomplete-listbox .MuiAutocomplete-option {
    width: 100% !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
    align-items: stretch !important;
    opacity: 1 !important;
  }

  .MuiAutocomplete-listbox .MuiAutocomplete-option:hover:not([aria-disabled="true"]),
  .MuiAutocomplete-listbox .MuiAutocomplete-option[data-focus="true"]:not([aria-disabled="true"]),
  .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused:not([aria-disabled="true"]),
  .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"]:not([aria-disabled="true"]) {
    background-color: #F3E8FF !important;
  }

  .MuiAutocomplete-option.Mui-disabled,
  .MuiAutocomplete-option[aria-disabled="true"],
  li[aria-disabled="true"].MuiAutocomplete-option {
    opacity: 1 !important;
    background-color: var(--grey-10, #F8F9FC) !important;
  }

  .MuiAutocomplete-option.Mui-disabled:hover,
  .MuiAutocomplete-option[aria-disabled="true"]:hover {
    background-color: var(--grey-10, #F8F9FC) !important;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar {
    width: 6px;
    flex-shrink: 0;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3.5px;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-thumb {
    background: var(--Greyscale---grey-60, #CDD4E3);
    border-radius: 3.5px;
    flex-shrink: 0;
  }
`;

/**
 * Injects global footer styles once into document.head.
 */
export const ensureMemberAutocompleteFooterStyles = () => {
  const existingStyle = document.getElementById(
    MEMBER_AUTOCOMPLETE_FOOTER_STYLE_ID,
  );
  if (existingStyle) {
    // Refresh during hot reloads so updated option styles are not stale.
    existingStyle.textContent = getMemberAutocompleteFooterStyles();
    return;
  }

  const style = document.createElement('style');
  style.id = MEMBER_AUTOCOMPLETE_FOOTER_STYLE_ID;
  style.textContent = getMemberAutocompleteFooterStyles();
  document.head.appendChild(style);
};

/**
 * @param {HTMLElement|null} dropdownPaper
 * @param {number} optionCount
 */
export const applyMemberAutocompletePaperStyles = (
  dropdownPaper,
  optionCount,
) => {
  if (!dropdownPaper) {
    return;
  }

  dropdownPaper.style.display = 'flex';
  dropdownPaper.style.paddingTop = '20px';
  dropdownPaper.style.flexDirection = 'column';
  dropdownPaper.style.alignItems = 'flex-start';
  dropdownPaper.style.alignSelf = 'stretch';
  dropdownPaper.style.borderRadius = '24px';
  dropdownPaper.style.background = '#FFF';
  dropdownPaper.style.boxShadow =
    '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)';
  dropdownPaper.style.position = 'relative';
  dropdownPaper.style.scrollbarWidth = 'thin';
  dropdownPaper.style.scrollbarColor =
    'var(--Greyscale---grey-60, #CDD4E3) transparent';

  if (optionCount === 0) {
    dropdownPaper.style.minHeight = '100px';
    dropdownPaper.style.overflowY = 'hidden';
  } else if (optionCount === 1) {
    dropdownPaper.style.minHeight = '170px';
    dropdownPaper.style.overflowY = 'hidden';
  } else {
    dropdownPaper.style.overflowY = 'auto';
  }
};

/**
 * @param {HTMLElement|null} listbox
 */
export const applyMemberAutocompleteListboxStyles = (listbox) => {
  if (!listbox) {
    return;
  }

  listbox.style.width = '100%';
  listbox.style.alignSelf = 'stretch';
  listbox.style.paddingTop = '0px';
  listbox.style.paddingBottom = '50px';
  listbox.style.paddingLeft = '0px';
  listbox.style.paddingRight = '0px';
  listbox.style.scrollbarWidth = 'thin';
  listbox.style.scrollbarColor =
    'var(--Greyscale---grey-60, #CDD4E3) transparent';
};

export const DEFAULT_MEMBER_AUTOCOMPLETE_FOOTER_LEFT_TEXT =
  'Be specific for quicker results';

/**
 * Creates or refreshes the dropdown footer (left helper text + result count).
 * @param {HTMLElement|null} dropdownPaper
 * @param {Array} memberOptions
 * @param {string} [footerLeftText]
 */
export const applyMemberAutocompleteFooterStyles = (
  dropdownPaper,
  memberOptions,
  footerLeftText = DEFAULT_MEMBER_AUTOCOMPLETE_FOOTER_LEFT_TEXT,
) => {
  if (!dropdownPaper) {
    return;
  }

  const existingFooter = dropdownPaper.querySelector('.dropdown-footer');
  if (existingFooter) {
    existingFooter.remove();
  }

  let gradientOverlay = dropdownPaper.querySelector(
    '.dropdown-gradient-overlay',
  );
  if (!gradientOverlay) {
    gradientOverlay = document.createElement('div');
    gradientOverlay.className = 'dropdown-gradient-overlay';
    gradientOverlay.style.cssText = `
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 50px;
      background: linear-gradient(to top, white 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
      pointer-events: none;
      z-index: 1;
    `;
    dropdownPaper.appendChild(gradientOverlay);
  }

  const footer = document.createElement('div');
  footer.className = 'dropdown-footer';
  footer.style.position = 'absolute';
  footer.style.bottom = '0';
  footer.style.left = '0';
  footer.style.right = '0';
  footer.style.background = 'white';
  footer.style.display = 'flex';
  footer.style.padding = '24px 20px 12px 20px';
  footer.style.justifyContent = 'flex-end';
  footer.style.alignItems = 'center';
  footer.style.gap = '4px';
  footer.style.alignSelf = 'stretch';
  footer.style.zIndex = '2';

  const footerLeft = document.createElement('div');
  footerLeft.className = 'footer-left';
  footerLeft.textContent =
    footerLeftText || DEFAULT_MEMBER_AUTOCOMPLETE_FOOTER_LEFT_TEXT;

  const footerRight = document.createElement('div');
  footerRight.className = 'footer-right';
  const resultCount = Array.isArray(memberOptions) ? memberOptions.length : 0;
  footerRight.textContent = `Showing ${resultCount}/${resultCount} Results`;

  footer.appendChild(footerLeft);
  footer.appendChild(footerRight);
  dropdownPaper.appendChild(footer);
};

/**
 * Applies paper, listbox, and footer styles for an open dropdown.
 * @param {HTMLElement|null} dropdownPaper
 * @param {HTMLElement|null} listbox
 * @param {Array} memberOptions
 * @param {string} [footerLeftText]
 */
export const setupMemberAutocompleteDropdownStyles = (
  dropdownPaper,
  listbox,
  memberOptions,
  footerLeftText,
) => {
  if (!dropdownPaper) {
    return;
  }

  const optionCount = Array.isArray(memberOptions) ? memberOptions.length : 0;
  applyMemberAutocompletePaperStyles(dropdownPaper, optionCount);
  applyMemberAutocompleteListboxStyles(listbox);
  applyMemberAutocompleteFooterStyles(
    dropdownPaper,
    memberOptions,
    footerLeftText,
  );
};

/**
 * Finds the open Autocomplete paper for a given root class.
 * @param {string} rootClassName
 * @returns {{ paper: HTMLElement|null, listbox: HTMLElement|null }}
 */
export const findMemberAutocompleteDropdown = (rootClassName) => {
  const rootSelector = `.${String(rootClassName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .join('.')}`;

  if (rootSelector === '.') {
    return { paper: null, listbox: null };
  }

  const autocompleteRoot = document.querySelector(rootSelector);
  if (!autocompleteRoot) {
    return { paper: null, listbox: null };
  }

  const input = autocompleteRoot.querySelector(
    '.MuiAutocomplete-inputRoot input',
  );
  const listboxId = input?.getAttribute('aria-controls');
  if (listboxId) {
    const listbox = document.getElementById(listboxId);
    const paper = listbox?.closest('.MuiAutocomplete-paper') || null;
    if (paper) {
      return { paper, listbox };
    }
  }

  const paper =
    autocompleteRoot.querySelector('.MuiAutocomplete-paper') ||
    document.querySelector('.MuiAutocomplete-paper');
  const listbox =
    paper?.querySelector('.MuiAutocomplete-listbox') || null;

  return { paper, listbox };
};
